import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import findKey from 'lodash/findKey';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import {
  PageTitle,
  PageContent,
  Tab,
  Link,
  Menu,
  Flex,
  Loader,
} from 'nallian-shared-ui/lib/components';
import { history } from 'nallian-shared-ui/lib/config/history';
import appService from '../../services/appService';
import { TABS, ACTIONS } from '../../constants';
import AppInfo from './AppInfo';
import EditApp from '../EditApp';
import AppInstallations from '../appInstallations/AppInstallations';
import AppInstallationDetail from '../appInstallations/AppInstallationDetail';
import appInstallationService from '../../services/appInstallationService';

const UPDATE_APP_RIGHT = 'apps/update';

const TAB_INDEX_MAPPING = {
  [TABS.DETAILS]: 0,
  [TABS.INSTALLATIONS]: 1,
};

const AppDetail = ({
  appFetch,
  dispatchAppGet,
  dispatchAppInstallationGet,
  appInstallationFetch,
}) => {
  const { t } = useTranslation();
  const { tab, id, installationId } = useParams();

  useEffect(() => {
    if (id) dispatchAppGet(id, undefined, t);
  }, [id, t, dispatchAppGet]);

  const appName = useMemo(
    () => (appFetch.value?.id === id && !appFetch.pending ? appFetch.value.name : '...'),
    [appFetch, id]
  );

  useEffect(() => {
    if (id && installationId && installationId !== appInstallationFetch.value?.id)
      dispatchAppInstallationGet(id, installationId, t);
  }, [t, id, installationId, dispatchAppInstallationGet, appInstallationFetch.value]);

  const panes = useMemo(
    () => [
      {
        menuItem: t('details'),
        render: () => (
          <Tab.Pane>
            <AppInfo appFetch={appFetch} />
          </Tab.Pane>
        ),
      },
      {
        menuItem: t('installations'),
        render: () => (
          <Tab.Pane>
            <AppInstallations appFetch={appFetch} />
          </Tab.Pane>
        ),
      },
      {
        menuItem: (
          <Menu.Menu position="right" key="menu">
            <HasRight right={UPDATE_APP_RIGHT}>
              {tab === TABS.DETAILS && id ? (
                <Menu.Item
                  icon="pencil"
                  content="Edit"
                  onClick={() => history.push(`/apps/${id}/edit`)}
                />
              ) : (
                <div />
              )}
            </HasRight>
          </Menu.Menu>
        ),
      },
    ],
    [appFetch, tab, t, id]
  );

  if (
    installationId &&
    appInstallationFetch.fulfilled &&
    appInstallationFetch.value?.id === installationId &&
    id === appFetch.value?.id
  ) {
    return (
      <PageContent>
        <AppInstallationDetail
          appName={appName}
          appFetch={appFetch}
          appInstallationFetch={appInstallationFetch}
        />
      </PageContent>
    );
  }

  return (
    <>
      <PageTitle flexed>
        <Flex alignItems="center">
          <Link to="/apps" href="/apps" marginright="1px">
            {t('apps_plural')}
          </Link>
          &nbsp;/&nbsp;
          {appName}
        </Flex>
      </PageTitle>
      <PageContent>
        {appFetch.pending ? (
          <Loader active={appFetch.pending} inline="centered" />
        ) : (
          <>
            {tab === ACTIONS.EDIT && (
              <HasRight right={UPDATE_APP_RIGHT}>
                <EditApp open={id && tab === ACTIONS.EDIT} appFetch={appFetch} />
              </HasRight>
            )}
            {appFetch.value?.id === id && (
              <Tab
                activeIndex={TAB_INDEX_MAPPING[tab]}
                onTabChange={(e, { activeIndex }) => {
                  const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
                  if (activeTab) {
                    history.push(`/apps/${id}/${activeTab}`);
                  }
                }}
                panes={panes}
              />
            )}
          </>
        )}
      </PageContent>
    </>
  );
};

AppDetail.propTypes = {
  dispatchAppGet: PropTypes.func.isRequired,
  appFetch: PropTypes.object.isRequired,
  dispatchAppInstallationGet: PropTypes.func.isRequired,
  appInstallationFetch: PropTypes.object.isRequired,
};

export default compose(appService, appInstallationService)(AppDetail);
